import React from 'react';
import { Heart, ShieldCheck, Users, Award, Target, Sparkles } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Passion for Play',
      description: 'We believe play is essential for childhood development and we\'re passionate about bringing joy to families.'
    },
    {
      icon: ShieldCheck,
      title: 'Safety First',
      description: 'Every toy in our collection meets or exceeds US safety standards. We never compromise on quality.'
    },
    {
      icon: Users,
      title: 'Family Focused',
      description: 'We\'re parents too! We understand what families need and curate products that bring everyone together.'
    },
    {
      icon: Award,
      title: 'Quality Guaranteed',
      description: 'We partner only with trusted manufacturers who share our commitment to excellence.'
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-sky-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 
              className="text-4xl md:text-5xl font-bold text-slate-800 mb-6"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              About <span className="text-sky-500">Cartlixs</span>
            </h1>
            <p 
              className="text-lg text-slate-600"
              style={{ fontFamily: 'Quicksand, sans-serif' }}
            >
              We're on a mission to fill every childhood with wonder, learning, and endless fun through carefully curated toys.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 
                className="text-3xl font-bold text-slate-800 mb-6"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
              >
                Our Story
              </h2>
              <div className="space-y-4 text-slate-600" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                <p>
                  Cartlixs was founded with a simple belief: every child deserves access to toys that are not only fun but also safe, educational, and built to last.
                </p>
                <p>
                  As parents ourselves, we understand the challenge of finding quality toys in a market flooded with cheap alternatives. That's why we personally test and curate every product in our collection.
                </p>
                <p>
                  Based in New York, we've grown from a small family operation to a trusted destination for parents across the United States. But our core values remain the same – quality, safety, and bringing smiles to children's faces.
                </p>
              </div>
            </div>
            <div className="rounded-3xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=600&h=500&fit=crop"
                alt="Children playing with toys"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-600 rounded-full px-4 py-2 mb-6">
              <Target size={18} />
              <span className="font-semibold">Our Mission</span>
            </div>
            <h2 
              className="text-3xl font-bold text-slate-800 mb-6"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              Inspiring Play, Sparking Imagination
            </h2>
            <p 
              className="text-lg text-slate-600"
              style={{ fontFamily: 'Quicksand, sans-serif' }}
            >
              Our mission is to provide families with toys that combine fun, education, and safety. We believe that through play, children develop crucial skills, express creativity, and build memories that last a lifetime. Every toy we offer is chosen with this purpose in mind.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl font-bold text-slate-800 mb-4"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              What We Stand For
            </h2>
            <p className="text-slate-600" style={{ fontFamily: 'Quicksand, sans-serif' }}>
              Our values guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition-shadow"
              >
                <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center mb-4">
                  <value.icon className="text-sky-500" size={28} />
                </div>
                <h3 
                  className="text-xl font-bold text-slate-800 mb-2"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                >
                  {value.title}
                </h3>
                <p className="text-slate-600" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-sky-500">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '50,000+', label: 'Happy Families' },
              { number: '500+', label: 'Products' },
              { number: '4.9', label: 'Average Rating' },
              { number: '99%', label: 'Satisfaction' },
            ].map((stat, index) => (
              <div key={index} className="text-center text-white">
                <p 
                  className="text-4xl md:text-5xl font-bold mb-2"
                  style={{ fontFamily: 'Fredoka, sans-serif' }}
                >
                  {stat.number}
                </p>
                <p className="text-sky-100" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-slate-50 rounded-3xl p-8 md:p-12 text-center">
            <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-600 rounded-full px-4 py-2 mb-6">
              <Sparkles size={18} />
              <span className="font-semibold">Our Promise</span>
            </div>
            <h2 
              className="text-3xl font-bold text-slate-800 mb-6"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
            >
              Our Commitment to You
            </h2>
            <p 
              className="text-lg text-slate-600 max-w-2xl mx-auto mb-8"
              style={{ fontFamily: 'Quicksand, sans-serif' }}
            >
              We stand behind every product we sell. If you're not completely satisfied with your purchase, we'll make it right. That's our promise to every family who shops with us.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center gap-2 text-slate-700">
                <ShieldCheck className="text-green-500" size={20} />
                <span className="font-medium">100% Safety Tested</span>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Heart className="text-red-500" size={20} />
                <span className="font-medium">30-Day Returns</span>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Award className="text-sky-500" size={20} />
                <span className="font-medium">Quality Guaranteed</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
